import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Grid,
  CircularProgress,
  Container,
  Paper
} from '@mui/material';
import { Helmet } from 'react-helmet';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ConnectWithoutContactIcon from '@mui/icons-material/ConnectWithoutContact';
import SecurityIcon from '@mui/icons-material/Security';
import SyncIcon from '@mui/icons-material/Sync';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { getDownloadURL, listAll, ref } from 'firebase/storage';
import { storage } from '../../../firebase/firebase';

// Components
import Navbar from '../../../components/Navbar/Navbar';
import Footer from '../../../components/Footer/Footer';
import Sidebar from '../../../components/Sidebar/Sidebar';

// Assets
import cleverIcon from '../../../../assets/img/Clever-icon.png';
import classlinkIcon from '../../../../assets/img/classlink-icon.png';

// Firebase
import {
  getAuth,
  browserSessionPersistence,
  setPersistence,
  signInWithCustomToken
} from 'firebase/auth';

// Utilities and constants
import { environment } from '../../../../environments/environment';
import RemoteErrorLog from '../../../Utils/api';
import { fetchCookieUsers } from '../../../Utils/firebase-services';

// Components
import CustomNoResponseModal from '../../../components/CustomNoResponseModal';
import DocumentViewerModal from '../../../components/DocumentViewerModal';

// Styles
import '../../../scss/Form.scss';

const StyledPaper = styled(Paper)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(4),
  borderRadius: theme.spacing(3),
  border: '2px solid #e2e8f0',
  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
  cursor: 'pointer',
  overflow: 'hidden',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: `
    0 4px 20px rgba(0, 0, 0, 0.08),
    0 2px 10px rgba(0, 0, 0, 0.05),
    0 1px 3px rgba(0, 0, 0, 0.1)
  `,

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '5px',
    background: 'linear-gradient(90deg, #2A9235, #4CAF50)',
    transform: 'scaleX(0)',
    transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  },

  '&:hover:not(.disabled)': {
    transform: 'translateY(-4px)',
    borderColor: '#2A9235',
    boxShadow: `
      0 15px 35px rgba(42, 146, 53, 0.15),
      0 8px 20px rgba(0, 0, 0, 0.1),
      0 4px 10px rgba(0, 0, 0, 0.08)
    `,
    background: 'linear-gradient(135deg, #ffffff 0%, #f0f9f2 100%)',

    '&::before': {
      transform: 'scaleX(1)',
    },
  },

  '&.disabled': {
    opacity: 0.6,
    cursor: 'not-allowed',
    background: '#f5f5f5',
  }
}));

const StyledConnectButton = styled(Button)(({ theme }) => ({
  width: '100%',
  height: '60px',
  padding: theme.spacing(2, 3),
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-start',
  textTransform: 'none',
  fontSize: '1rem',
  fontWeight: 600,
  gap: theme.spacing(2),
  borderRadius: theme.spacing(4), // More rounded like the example
  border: '2px solid #e2e8f0',
  background: '#ffffff',
  color: '#1a1a1a',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.06)',

  '&:hover:not(:disabled)': {
    borderColor: '#2A9235',
    background: '#ffffff',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 20px rgba(42, 146, 53, 0.15)',

    '& .platform-logo': {
      transform: 'scale(1.05)',
    }
  },

  '&:disabled': {
    opacity: 0.5,
    background: '#f5f5f5',
    cursor: 'not-allowed',
  }
}));


const BackButton = styled(Button)(({ theme }) => ({
  color: '#2A9235',
  textTransform: 'none',
  fontWeight: 600,
  padding: theme.spacing(1, 2),
  borderRadius: theme.spacing(2),
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(42, 146, 53, 0.08)',
    transform: 'translateX(-4px)',
  }
}));

const FeatureBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1.5),
  backgroundColor: 'rgba(42, 146, 53, 0.05)',
  marginBottom: theme.spacing(2),
  transition: 'all 0.3s ease',

  '&:hover': {
    backgroundColor: 'rgba(42, 146, 53, 0.1)',
    transform: 'translateX(4px)',
  }
}));

const SchoolConnect: React.FC = () => {
  const navigate = useNavigate();

  // State management
  const [isChecked, setIsChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [firebaseUser, setFirebaseUser] = useState<any>();
  const [firebaseUserProvider, setFirebaseUserProvider] = useState('');

  // Modal states
  const [isQuestionModalVisible, setIsQuestionModalVisible] = useState(false);
  const [apiErrorMessage, setApiErrorMessage] = useState('');
  const [privacyPolicyOpen, setPrivacyPolicyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);

  // Document URLs for privacy policy and terms
  const [documentUrls, setDocumentUrls] = useState<{
    privacyPolicy: string | null;
    termsAndConditions: string | null;
  }>({
    privacyPolicy: null,
    termsAndConditions: null
  });

  // Fetch policy documents from Firebase Storage
  const fetchPages = async () => {
    try {
      const storageRef = await ref(storage, 'pages');
      const result = await listAll(storageRef);
      const urls = await Promise.all(
        result.items.map((pages) => getDownloadURL(pages))
      );

      setDocumentUrls({
        termsAndConditions: urls[2] || null,
        privacyPolicy: urls[1] || null
      });
    } catch (e) {
      RemoteErrorLog(e, 'SchoolConnect.tsx');
    }
  };

  useEffect(() => {
    fetchPages();
    window.scrollTo(0, 0);
  }, []);

  // Navigation handlers
  const handleBack = () => {
    navigate('/signup-school-select');
  };

  const handlePolicyClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (documentUrls.privacyPolicy) {
      setPrivacyPolicyOpen(true);
    } else {
      console.error('Privacy Policy document not available');
    }
  };

  const handleTermsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (documentUrls.termsAndConditions) {
      setTermsOpen(true);
    } else {
      console.error('Terms & Conditions document not available');
    }
  };

  const hideNoQuestionModal = () => {
    setIsQuestionModalVisible(false);
  };

  const handleCleverSignup = async () => {
    if (!isChecked) {
      setApiErrorMessage('Please accept the terms and conditions before proceeding.');
      setIsQuestionModalVisible(true);
      return;
    }

    try {
      setIsLoading(true);
      const auth = getAuth();
      await setPersistence(auth, browserSessionPersistence);

      const width = 500;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        'about:blank',
        'Clever Signup',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      if (!popup) {
        throw new Error('Popup blocked. Please enable popups and try again.');
      }

      const response = await axios.get(`${environment.backendUrl}oauth/authorize`);
      popup.location.replace(response.data.login_url);

      const userInfo = await new Promise<{
        user: any;
        first_login: boolean;
        spelling_preference_set: boolean;
      }>((resolve, reject) => {
        const popupCheckInterval = setInterval(() => {
          if (popup.closed) {
            clearInterval(popupCheckInterval);
            window.removeEventListener('message', handleMessage);
            reject(new Error('Signup cancelled'));
          }
        }, 500);

        const handleMessage = async (event: MessageEvent) => {
          if (event.data.error) {
            clearInterval(popupCheckInterval);
            reject(new Error(event.data.message || 'Error during Clever signup'));
            return;
          }

          if (event.data.token && event.data.user_id) {
            try {
              clearInterval(popupCheckInterval);
              const credential = await signInWithCustomToken(auth, event.data.token);
              resolve({
                user: credential.user,
                first_login: event.data.first_login,
                spelling_preference_set: event.data.spelling_preference_set
              });
            } catch (error) {
              reject(error);
            } finally {
              popup.close();
              window.removeEventListener('message', handleMessage);
            }
          }
        };

        window.addEventListener('message', handleMessage);

        setTimeout(() => {
          clearInterval(popupCheckInterval);
          window.removeEventListener('message', handleMessage);
          popup.close();
          reject(new Error('Signup timed out. Please try again.'));
        }, 60000);
      });

      localStorage.setItem('userData', JSON.stringify(userInfo.user));
      const { uid, isAnonymous, displayName } = userInfo.user;
      const userCookiePreference = await fetchCookieUsers(uid);

      if (userCookiePreference === 'Error') {
        setIsQuestionModalVisible(true);
        setApiErrorMessage(
          "We're currently experiencing some issues. Please try again later."
        );
        localStorage.removeItem('userData');
      } else if (userCookiePreference === false) {
        setFirebaseUser({ uid, isAnonymous, displayName });
      } else if (userInfo.first_login || !userInfo.spelling_preference_set) {
        navigate('/home', { state: { showSpellingPrefModal: true } });
      } else {
        navigate('/home');
      }

      setFirebaseUserProvider('clever.com');
    } catch (error: any) {
      if (error.message === 'Signup cancelled') {
        return;
      }

      RemoteErrorLog(error, 'SchoolConnect.tsx');

      if (error.message.includes("Your Clever account does not exist")) {
        setIsQuestionModalVisible(true);
        setApiErrorMessage(error.message);
      } else if (error.message === 'Signup timed out. Please try again.') {
        setApiErrorMessage(error.message);
        setIsQuestionModalVisible(true);
      } else {
        setApiErrorMessage('Failed to authenticate with Clever. Please try again.');
        setIsQuestionModalVisible(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClasslinkSignup = async () => {
    if (!isChecked) {
      setApiErrorMessage('Please accept the terms and conditions before proceeding.');
      setIsQuestionModalVisible(true);
      return;
    }

    try {
      setIsLoading(true);
      const auth = getAuth();
      await setPersistence(auth, browserSessionPersistence);

      const width = 500;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        'about:blank',
        'ClassLink Signup',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      if (!popup) {
        throw new Error('Popup blocked. Please enable popups and try again.');
      }

      const response = await axios.get(`${environment.backendUrl}classlink/authorize`);
      popup.location.replace(response.data.login_url);

      const userInfo = await new Promise<{
        user: any;
        first_login: boolean;
        spelling_preference_set: boolean;
      }>((resolve, reject) => {
        const popupCheckInterval = setInterval(() => {
          if (popup.closed) {
            clearInterval(popupCheckInterval);
            window.removeEventListener('message', handleMessage);
            reject(new Error('Signup cancelled'));
          }
        }, 500);

        const handleMessage = async (event: MessageEvent) => {
          if (event.data.error) {
            clearInterval(popupCheckInterval);
            reject(new Error(event.data.message || 'Error during ClassLink signup'));
            return;
          }

          if (event.data.token && event.data.user_id) {
            try {
              clearInterval(popupCheckInterval);
              const credential = await signInWithCustomToken(auth, event.data.token);
              resolve({
                user: credential.user,
                first_login: event.data.first_login,
                spelling_preference_set: event.data.spelling_preference_set
              });
            } catch (error) {
              reject(error);
            } finally {
              popup.close();
              window.removeEventListener('message', handleMessage);
            }
          }
        };

        window.addEventListener('message', handleMessage);

        setTimeout(() => {
          clearInterval(popupCheckInterval);
          window.removeEventListener('message', handleMessage);
          popup.close();
          reject(new Error('Signup timed out. Please try again.'));
        }, 60000);
      });

      localStorage.setItem('userData', JSON.stringify(userInfo.user));
      const { uid, isAnonymous, displayName } = userInfo.user;
      const userCookiePreference = await fetchCookieUsers(uid);

      if (userCookiePreference === 'Error') {
        setIsQuestionModalVisible(true);
        setApiErrorMessage(
          "We're currently experiencing some issues. Please try again later."
        );
        localStorage.removeItem('userData');
      } else if (userCookiePreference === false) {
        setFirebaseUser({ uid, isAnonymous, displayName });
      } else if (userInfo.first_login || !userInfo.spelling_preference_set) {
        navigate('/home', { state: { showSpellingPrefModal: true } });
      } else {
        navigate('/home');
      }

      setFirebaseUserProvider('classlink.com');
    } catch (error: any) {
      if (error.message === 'Signup cancelled') {
        return;
      }

      RemoteErrorLog(error, 'SchoolConnect.tsx');

      if (error.message.includes("Your ClassLink account does not exist")) {
        setIsQuestionModalVisible(true);
        setApiErrorMessage(error.message);
      } else if (error.message === 'Signup timed out. Please try again.') {
        setApiErrorMessage(error.message);
        setIsQuestionModalVisible(true);
      } else {
        setApiErrorMessage('Failed to authenticate with ClassLink. Please try again.');
        setIsQuestionModalVisible(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Connect Your School System - Spelling Hero</title>
        <meta name="description" content="Connect your school's Clever or ClassLink account to quickly sync your classroom and student roster with Spelling Hero." />
        <meta name="keywords" content="clever login, classlink login, school connect, student roster sync" />
      </Helmet>

      <header>
        <Navbar currentPage="signup" />
      </header>

      <main className="main-container" style={{ paddingTop: 0 }}>
        <section className="quiz-signup">
          <Box sx={{ padding: 0 }}>
            <Grid container spacing={2}>
              <Grid
                item
                lg={5}
                xs={12}
                sm={12}
                sx={{ paddingLeft: 0 }}
                className="sidebar-top-spacing"
              >
                <Sidebar />
              </Grid>

              <Grid item xs={12} sm={12} lg={7} xl={7}>
                <Box sx={{
                  position: 'relative',
                  paddingLeft: { xs: 2, sm: 3, md: 4 },
                  paddingRight: { xs: 2, sm: 3, md: 4 },
                  paddingTop: 2,
                  paddingBottom: 4,
                  marginTop: { xs: 2, md: 4 }
                }}>
                  {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                      <CircularProgress size={60} />
                    </Box>
                  ) : (
                    <>
                      {/* Back Button - positioned close to sidebar */}
                      <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 1 }}>
                        <BackButton startIcon={<ArrowBackIcon />} onClick={handleBack} disabled={isLoading}>Back</BackButton>
                      </Box>

                      <Box sx={{ pt: 4 }}>
                        <Container maxWidth="md" sx={{ py: 2 }}>
                          {/* Header Section */}
                          <Box sx={{ textAlign: 'center', mb: 6 }}>
                            <Typography
                              variant="h2"
                              component="h1"
                              sx={{
                                mb: 2,
                                fontWeight: 700,
                                background: 'linear-gradient(135deg, #2A9235 0%, #4CAF50 100%)',
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                color: 'test.secondary',
                                fontSize: { xs: '2rem', md: '2.5rem' }
                              }}
                            >
                              Connect with Your School System
                            </Typography>

                            <Box
                              sx={{
                                width: 60,
                                height: 4,
                                background: 'linear-gradient(90deg, #2A9235, #4CAF50)',
                                borderRadius: 2,
                                mx: 'auto'
                              }}
                            />
                          </Box>

                          {/* Features Section */}
                          <Box sx={{ mb: 5 }}>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={4}>
                                <FeatureBox>
                                  <SyncIcon sx={{ color: '#2A9235', mr: 2 }} />
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    Auto-sync
                                  </Typography>
                                </FeatureBox>
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <FeatureBox>
                                  <SecurityIcon sx={{ color: '#2A9235', mr: 2 }} />
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    Secure login
                                  </Typography>
                                </FeatureBox>
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <FeatureBox>
                                  <ConnectWithoutContactIcon sx={{ color: '#2A9235', mr: 2 }} />
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    Quick setup
                                  </Typography>
                                </FeatureBox>
                              </Grid>
                            </Grid>
                          </Box>

                          {/* Terms & Conditions */}
                          <Box sx={{ mb: 4 }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={isChecked}
                                  onChange={(e) => setIsChecked(e.target.checked)}
                                  name="termsCheckbox"
                                  disabled={isLoading}
                                  sx={{
                                    color: '#2A9235',
                                    '&.Mui-checked': {
                                      color: '#2A9235',
                                    }
                                  }}
                                />
                              }
                              label={
                                <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                                  By clicking here, I agree to the{' '}
                                  <Button
                                    variant="text"
                                    sx={{
                                      p: 0,
                                      textTransform: 'none',
                                      textDecoration: 'underline',
                                      color: '#2A9235',
                                      fontWeight: 600,
                                      minWidth: 'auto'
                                    }}
                                    onClick={handlePolicyClick}
                                    disabled={isLoading}
                                  >
                                    Privacy Policy
                                  </Button>
                                  {' '}and{' '}
                                  <Button
                                    variant="text"
                                    sx={{
                                      p: 0,
                                      textTransform: 'none',
                                      textDecoration: 'underline',
                                      color: '#2A9235',
                                      fontWeight: 600,
                                      minWidth: 'auto'
                                    }}
                                    onClick={handleTermsClick}
                                    disabled={isLoading}
                                  >
                                    Terms & Conditions
                                  </Button>
                                  .
                                </Typography>
                              }
                            />
                          </Box>

                          {/* Connect Buttons */}
                          <Grid container spacing={3}>
                            <Grid item xs={12}>
                              <StyledConnectButton
                                onClick={handleCleverSignup}
                                disabled={!isChecked || isLoading}
                                className={(!isChecked || isLoading) ? 'disabled' : ''}
                              >
                                {isLoading ? (
                                  <CircularProgress size={24} sx={{ color: '#2A9235' }} />
                                ) : (
                                  <>
                                    <img
                                      src={cleverIcon}
                                      alt="Clever"
                                      className="platform-logo"
                                      style={{
                                        height: 32,
                                        width: 32,
                                        transition: 'transform 0.3s ease'
                                      }}
                                    />
                                    <Typography variant="body1" sx={{ fontWeight: 600, flex: 1, textAlign: 'center' }}>
                                      Sign up with Clever
                                    </Typography>
                                  </>
                                )}
                              </StyledConnectButton>
                            </Grid>

                            <Grid item xs={12}>
                              <StyledConnectButton
                                onClick={handleClasslinkSignup}
                                disabled={!isChecked || isLoading}
                                className={(!isChecked || isLoading) ? 'disabled' : ''}
                              >
                                {isLoading ? (
                                  <CircularProgress size={24} sx={{ color: '#2A9235' }} />
                                ) : (
                                  <>
                                    <img
                                      src={classlinkIcon}
                                      alt="ClassLink"
                                      className="platform-logo"
                                      style={{
                                        height: 32,
                                        width: 32,
                                        transition: 'transform 0.3s ease'
                                      }}
                                    />
                                    <Typography variant="body1" sx={{ fontWeight: 600, flex: 1, textAlign: 'center' }}>
                                      Sign up with ClassLink
                                    </Typography>
                                  </>
                                )}
                              </StyledConnectButton>
                            </Grid>
                          </Grid>

                          {/* Help Section */}
                          <Box sx={{
                            mt: 5,
                            p: { xs: 2, md: 3 },
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                            border: '1px solid #e2e8f0',
                            textAlign: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            maxWidth: 640,
                            mx: 'auto'
                          }}>
                            <SecurityIcon sx={{ color: '#2A9235', mb: 1.5, fontSize: 34 }} />
                            <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 560 }}>
                              Your login credentials are handled securely by your school's authentication system. We never store your school passwords.
                            </Typography>
                          </Box>
                        </Container>
                      </Box>
                    </>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </section>
      </main>

      <Footer />

      {/* Modals */}
      <CustomNoResponseModal
        isModalVisible={isQuestionModalVisible}
        onPress={hideNoQuestionModal}
        buttonText="OK"
        modalTitle="Oops!"
        text={apiErrorMessage}
      />

      <DocumentViewerModal
        open={privacyPolicyOpen}
        onClose={() => setPrivacyPolicyOpen(false)}
        documentUrl={documentUrls.privacyPolicy}
        title="Privacy Policy"
      />

      <DocumentViewerModal
        open={termsOpen}
        onClose={() => setTermsOpen(false)}
        documentUrl={documentUrls.termsAndConditions}
        title="Terms & Conditions"
      />
    </>
  );
};

export default SchoolConnect;
